import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('stockops_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('stockops_token');
      localStorage.removeItem('stockops_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Safari/PWA compatible file download helper
export const downloadFile = async (url, filename) => {
  try {
    const token = localStorage.getItem('stockops_token');
    const response = await fetch(`${API_BASE}${url}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      cache: 'no-store',
    });
    
    if (!response.ok) {
      throw new Error('Download failed');
    }
    
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    
    // Create invisible link and trigger download
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    
    // Use click() for Safari compatibility
    link.click();
    
    // Cleanup
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    }, 100);
    
    return true;
  } catch (error) {
    console.error('Download error:', error);
    throw error;
  }
};

// Auth
export const login = (accessCode) => api.post('/auth/login', { access_code: accessCode });
export const getMe = () => api.get('/auth/me');

// Users
export const getUsers = () => api.get('/users');
export const createUser = (data) => api.post('/users', data);
export const rotateUserCode = (userId, newCode) => api.put(`/users/${userId}/rotate-code?new_code=${encodeURIComponent(newCode)}`);
export const toggleUserActive = (userId) => api.put(`/users/${userId}/toggle-active`);

// Products
export const getProducts = (activeOnly = false) => api.get(`/products?active_only=${activeOnly}`);
export const getProductsWithBalance = (activeOnly = false) => api.get(`/products/with-balance?active_only=${activeOnly}`);
export const getQuickPicks = () => api.get('/products/quick-picks');
export const searchProducts = (query) => api.get(`/products/search?q=${encodeURIComponent(query)}`);
export const getProductByBarcode = (barcode) => api.get(`/products/barcode/${encodeURIComponent(barcode)}`);
export const getProduct = (productId) => api.get(`/products/${productId}`);
export const createProduct = (data) => api.post('/products', data);
export const updateProduct = (productId, data) => api.put(`/products/${productId}`, data);
export const deleteProduct = (productId) => api.delete(`/products/${productId}`);
export const bulkStocktake = (items) => api.post('/products/bulk-stocktake', { items });
export const importProducts = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/products/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Export functions using Safari-compatible download
export const exportProducts = () => downloadFile('/products/export/csv', 'products_export.csv');
export const exportRestockNeeded = () => downloadFile('/export/restock-needed', 'restock_needed.csv');
export const exportAuditLogs = (startDate, endDate) => {
  const params = new URLSearchParams();
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  const query = params.toString() ? `?${params.toString()}` : '';
  return downloadFile(`/export/audit-logs${query}`, 'audit_logs.csv');
};

// Stock Movements
export const logStockOut = (data) => api.post('/stock-out', data);
export const getStockMovements = (cycleId, productId, limit = 100) => {
  const params = new URLSearchParams();
  if (cycleId) params.append('cycle_id', cycleId);
  if (productId) params.append('product_id', productId);
  params.append('limit', limit);
  return api.get(`/stock-movements?${params.toString()}`);
};
export const voidMovement = (movementId) => api.put(`/stock-movements/${movementId}/void`);

// Restock
export const getRestockQueue = () => api.get('/restock/queue');
export const restockItem = (productId, quantity) => api.post('/restock', { product_id: productId, quantity });
export const restockAll = () => api.post('/restock/all');

// Restock Cycles
export const getCurrentCycle = () => api.get('/restock-cycles/current');
export const getRestockCycles = () => api.get('/restock-cycles');
export const closeCurrentCycle = () => api.post('/restock-cycles/close');

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');
export const getRecentActivity = (limit = 10) => api.get(`/dashboard/recent-activity?limit=${limit}`);

// App Settings
export const getSettings = () => api.get('/settings');
export const updateSettings = (settings) => api.put('/settings', settings);

// Seed
export const seedData = () => api.post('/seed');

export default api;
